// ManualTargetsVisualizer.jsx

import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import {
    Box,
    Button,
    IconButton,
    Skeleton,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from "@mui/material";

import AddIcon from "@mui/icons-material/Add";
import BarChartIcon from "@mui/icons-material/BarChart";
import TableChartIcon from "@mui/icons-material/TableChart";
import ViewCompactIcon from "@mui/icons-material/ViewCompact";
import ViewComfyIcon from "@mui/icons-material/ViewComfy";

import EChartsReact from "echarts-for-react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import { getMetricName } from "../helpers/filters";
import {
    getActivePeriodForDate,
    getInterpolatedManualValue,
    getLastYearValue,
    getStepUnit,
    normalizePointDate,
    normalizeValidFromDate,
    TARGET_DATE_TYPES,
    TARGET_GRANULARITY_DAY,
    TARGET_GRANULARITY_MONTH,
    TARGET_GRANULARITY_YEAR,
    TARGET_MODES,
    TARGET_TYPES,
} from "../helpers/targets";
import Colours from "../helpers/colours";

dayjs.extend(utc);

export const ManualTargetsViewModeToggle = ({ value, onChange }) => {
    return (
        <Stack spacing={0.5}>
            <Typography>View</Typography>
            <ToggleButtonGroup
                exclusive
                size="small"
                value={value}
                onChange={(event, nextValue) => {
                    if (nextValue) onChange(nextValue);
                }}
            >
                <Tooltip title="Show selected metric as chart">
                    <ToggleButton value="chart" aria-label="Chart view">
                        <BarChartIcon fontSize="small" />
                    </ToggleButton>
                </Tooltip>

                <Tooltip title="Show all metrics in table">
                    <ToggleButton value="table" aria-label="Table view">
                        <TableChartIcon fontSize="small" />
                    </ToggleButton>
                </Tooltip>
            </ToggleButtonGroup>
        </Stack>
    );
};

const round2 = (value) => Number(Number(value ?? 0).toFixed(2));

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const asUtcDay = (value) => {
    if (!value) return null;
    return dayjs.isDayjs(value) ? value.utc() : dayjs.utc(value);
};

const normalizeRangeStart = (value, dateGranularity) => {
    const base = asUtcDay(value);
    if (!base) return null;

    if (dateGranularity === TARGET_GRANULARITY_MONTH)
        return base.startOf("month");
    if (dateGranularity === TARGET_GRANULARITY_YEAR)
        return base.startOf("year");
    return base.startOf("day");
};

const normalizeRangeEnd = (value, dateGranularity) => {
    const base = asUtcDay(value);
    if (!base) return null;

    if (dateGranularity === TARGET_GRANULARITY_MONTH)
        return base.endOf("month");
    if (dateGranularity === TARGET_GRANULARITY_YEAR) return base.endOf("year");
    return base.startOf("day");
};

const formatDisplayDate = (value, dateGranularity) => {
    const date = asUtcDay(value);
    if (!date) return "";

    if (dateGranularity === TARGET_GRANULARITY_MONTH)
        return date.format("MMM YYYY");
    if (dateGranularity === TARGET_GRANULARITY_YEAR) return date.format("YYYY");
    return date.format("DD MMM YYYY");
};

const formatTooltipNumber = (value) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return "—";
    }

    return round2(value);
};

const buildAxisDates = (fromDate, toDate, dateGranularity) => {
    const start = normalizeRangeStart(fromDate, dateGranularity);
    const end = normalizeRangeEnd(toDate, dateGranularity);

    if (!start || !end || end.isBefore(start, "day")) return [];

    const unit = getStepUnit(dateGranularity);
    const dates = [];
    let cursor = start;

    while (!cursor.isAfter(end, unit)) {
        if (dateGranularity === TARGET_GRANULARITY_MONTH) {
            dates.push(cursor.startOf("month"));
        } else if (dateGranularity === TARGET_GRANULARITY_YEAR) {
            dates.push(cursor.startOf("year"));
        } else {
            dates.push(cursor.startOf("day"));
        }

        cursor = cursor.add(1, unit);
    }

    return dates;
};

const getModeLabel = (modeValue) => {
    const match = TARGET_MODES.find(
        (option) =>
            option.value === String(modeValue).toLowerCase() ||
            option.apiValue === String(modeValue).toUpperCase(),
    );

    return match?.label ?? modeValue ?? "—";
};

const getTargetSubtypeLabel = (value) => getMetricName(value) ?? value;

const getDateGranularityLabel = (value) => {
    return (
        TARGET_DATE_TYPES.find((item) => item.value === value)?.label ?? value
    );
};

const getDateKey = (date, dateGranularity) => {
    if (dateGranularity === TARGET_GRANULARITY_MONTH) {
        return date.startOf("month").format("YYYY-MM-DD");
    }

    if (dateGranularity === TARGET_GRANULARITY_YEAR) {
        return date.startOf("year").format("YYYY-MM-DD");
    }

    return date.startOf("day").format("YYYY-MM-DD");
};

const getModeColor = (mode) => {
    if (mode === "MANUAL") return "rgba(33, 150, 243, 0.14)";
    if (mode === "COMBINED") return "rgba(156, 39, 176, 0.14)";
    if (mode === "PERCENTAGE") return "rgba(76, 175, 80, 0.14)";
    return "rgba(255,255,255,0.03)";
};

const getModeShortLabel = (mode, percentage) => {
    if (mode === "PERCENTAGE") return `P${Number(percentage ?? 0)}%`;
    if (mode === "COMBINED") return `C${Number(percentage ?? 0)}%`;
    if (mode === "MANUAL") return "M";
    return "—";
};

const getTargetTypeLabel = (value) => {
    return TARGET_TYPES.find((item) => item.value === value)?.label ?? value;
};

const ManualTargetsVisualizer = ({
    loading,
    viewMode,
    chartFromDate,
    chartToDate,
    dateGranularity,
    targetType,
    targetSubtype,
    targetSubtypes,
    manualTargets,
    periodRows,
    calculatedData,
    validFrom,
    percentageN,
    sanitizedHotelID,
    onCreatePoint,
    onEditPoint,
    onDeletePoint,
}) => {
    const chartRef = useRef(null);
    const [hoveredPoint, setHoveredPoint] = useState(null);
    const [tableDensity, setTableDensity] = useState("compact");
    const tableFirstColumnWidth = 190;
    const tableDayColumnWidth = 78;
    const tableHeaderRowHeight = 42;

    const metricOptions = useMemo(() => {
        const values =
            Array.isArray(targetSubtypes) && targetSubtypes.length
                ? targetSubtypes
                : [targetSubtype];

        return [...new Set([targetSubtype, ...values])]
            .filter(Boolean)
            .map((value) => ({
                value,
                label: getTargetSubtypeLabel(value),
                selected: value === targetSubtype,
            }));
    }, [targetSubtype, targetSubtypes]);

    const buildSeriesForMetric = useCallback(
        (metricSubtype) => {
            const axisDates = buildAxisDates(
                chartFromDate,
                chartToDate,
                dateGranularity,
            );
            const axisKeys = axisDates.map((date) => date.format("YYYY-MM-DD"));

            const start = normalizeRangeStart(chartFromDate, dateGranularity);
            const end = normalizeRangeEnd(chartToDate, dateGranularity);

            const normalizeManualPointRow = (row) => ({
                ...row,
                parsedDate: normalizePointDate(
                    row.TargetDate,
                    row.DateGranularity || dateGranularity,
                ),
            });

            const isCurrentManualPoint = (row) => {
                return (
                    String(row.HotelID ?? row.hotel_id ?? sanitizedHotelID) ===
                        String(sanitizedHotelID) &&
                    String(row.TargetType ?? "").trim() === targetType.trim() &&
                    String(row.TargetSubType ?? "").trim() ===
                        String(metricSubtype).trim() &&
                    String(row.DateGranularity ?? "")
                        .trim()
                        .toUpperCase() === dateGranularity
                );
            };

            const isCurrentPeriod = (row) => {
                return (
                    String(row.HotelID ?? row.hotel_id ?? sanitizedHotelID) ===
                        String(sanitizedHotelID) &&
                    String(row.TargetType ?? "").trim() === targetType.trim() &&
                    String(row.TargetSubType ?? "").trim() ===
                        String(metricSubtype).trim() &&
                    String(row.DateGranularity ?? "")
                        .trim()
                        .toUpperCase() === dateGranularity
                );
            };

            const isInsideRange = (row) => {
                return (
                    row.parsedDate &&
                    start &&
                    end &&
                    !row.parsedDate.isBefore(start, "day") &&
                    !row.parsedDate.isAfter(end, "day")
                );
            };

            const sortedManualPointsForInterpolation = [...manualTargets]
                .map(normalizeManualPointRow)
                .filter((row) => row.parsedDate && isCurrentManualPoint(row))
                .sort((left, right) =>
                    left.parsedDate.isBefore(right.parsedDate, "day") ? -1 : 1,
                );

            const sortedManualPoints =
                sortedManualPointsForInterpolation.filter(isInsideRange);

            const exactManualPointMap = new Map();

            sortedManualPointsForInterpolation.forEach((row) => {
                exactManualPointMap.set(
                    getDateKey(row.parsedDate, dateGranularity),
                    row,
                );
            });

            const sortedPeriods = [...periodRows]
                .map((row) => ({
                    ...row,
                    parsedValidFrom: normalizeValidFromDate(
                        row.ValidFrom,
                        row.DateGranularity || dateGranularity,
                    ),
                }))
                .filter((row) => !!row.parsedValidFrom && isCurrentPeriod(row))
                .sort((left, right) =>
                    left.parsedValidFrom.isBefore(right.parsedValidFrom, "day")
                        ? -1
                        : 1,
                );

            const activePeriods = axisDates.map((axisDate) =>
                getActivePeriodForDate(
                    sortedPeriods,
                    axisDate,
                    dateGranularity,
                ),
            );

            const manualInterpolatedData = axisDates.map((axisDate) =>
                getInterpolatedManualValue(
                    axisDate,
                    sortedManualPointsForInterpolation,
                    dateGranularity,
                ),
            );

            const draftValidFrom = normalizeValidFromDate(
                validFrom,
                dateGranularity,
            );

            const percentagePreviewData = axisDates.map((axisDate, index) => {
                const activePeriod = activePeriods[index];

                if (activePeriod) {
                    return getLastYearValue(
                        axisDate,
                        Number(activePeriod.PercentageN ?? 0),
                        metricSubtype,
                        calculatedData,
                        dateGranularity,
                    );
                }

                const percentageToUse =
                    draftValidFrom && !draftValidFrom.isAfter(axisDate, "day")
                        ? Number(percentageN ?? 0)
                        : 0;

                return getLastYearValue(
                    axisDate,
                    percentageToUse,
                    metricSubtype,
                    calculatedData,
                    dateGranularity,
                );
            });

            const calculatedTargetData = axisDates.map((axisDate, index) => {
                const activePeriod = activePeriods[index];
                if (!activePeriod) return null;

                const mode = String(
                    activePeriod.TargetMode || "",
                ).toUpperCase();
                const key = getDateKey(axisDate, dateGranularity);

                if (mode === "MANUAL") {
                    return manualInterpolatedData[index];
                }

                if (mode === "PERCENTAGE") {
                    return percentagePreviewData[index];
                }

                if (mode === "COMBINED") {
                    if (exactManualPointMap.has(key)) {
                        return round2(exactManualPointMap.get(key).TargetValue);
                    }

                    return percentagePreviewData[index];
                }

                return null;
            });

            const manualPointsScatterData = sortedManualPoints.map((row) => ({
                value: [
                    getDateKey(row.parsedDate, dateGranularity),
                    round2(row.TargetValue),
                ],
            }));

            let lastBeforeRange = null;
            const periodsInsideRange = [];

            sortedPeriods.forEach((row) => {
                if (row.parsedValidFrom.isBefore(start, "day")) {
                    lastBeforeRange = row;
                } else if (!row.parsedValidFrom.isAfter(end, "day")) {
                    periodsInsideRange.push(row);
                }
            });

            const periodsForBlocks = lastBeforeRange
                ? [lastBeforeRange, ...periodsInsideRange]
                : periodsInsideRange;

            const periodBlocks = periodsForBlocks
                .map((period, index) => {
                    const nextPeriod = periodsForBlocks[index + 1];

                    const blockStart = period.parsedValidFrom.isBefore(
                        start,
                        "day",
                    )
                        ? start
                        : period.parsedValidFrom;

                    const blockEnd = nextPeriod
                        ? nextPeriod.parsedValidFrom
                        : end;

                    if (blockEnd.isBefore(blockStart, "day")) return null;

                    return {
                        ...period,
                        blockStart: getDateKey(blockStart, dateGranularity),
                        blockEnd: getDateKey(blockEnd, dateGranularity),
                    };
                })
                .filter(Boolean);

            return {
                axisDates,
                axisKeys,
                activePeriods,
                exactManualPointMap,
                periodBlocks,
                calculatedTargetData,
                percentagePreviewData,
                manualPointsScatterData,
            };
        },
        [
            calculatedData,
            chartFromDate,
            chartToDate,
            dateGranularity,
            manualTargets,
            percentageN,
            periodRows,
            sanitizedHotelID,
            targetType,
            validFrom,
        ],
    );

    const chartSeries = useMemo(
        () => buildSeriesForMetric(targetSubtype),
        [buildSeriesForMetric, targetSubtype],
    );

    const tableRows = useMemo(() => {
        if (viewMode !== "table") return [];

        return metricOptions.map((metric) => ({
            ...metric,
            series:
                metric.value === targetSubtype
                    ? chartSeries
                    : buildSeriesForMetric(metric.value),
        }));
    }, [
        buildSeriesForMetric,
        chartSeries,
        metricOptions,
        targetSubtype,
        viewMode,
    ]);

    const chartOption = useMemo(() => {
        const markAreaData = chartSeries.periodBlocks.map((period) => {
            return [
                {
                    name: `${getModeLabel(period.TargetMode)} · ${Number(
                        period.PercentageN ?? 0,
                    )}%`,
                    xAxis: period.blockStart,
                    itemStyle: {
                        color: getModeColor(
                            String(period.TargetMode || "").toUpperCase(),
                        ),
                    },
                },
                {
                    xAxis: period.blockEnd,
                },
            ];
        });

        return {
            animation: false,
            legend: {
                top: 0,
                data: ["Target", "Percentage preview", "Manual points"],
            },
            dataZoom: [
                {
                    type: "inside",
                },
                {
                    type: "slider",
                },
            ],
            tooltip: {
                trigger: "axis",
                axisPointer: {
                    type: "cross",
                    label: {
                        formatter: (params) => {
                            if (params.axisDimension === "y") {
                                return `${Math.round(Number(params.value ?? 0))}`;
                            }

                            return params.value ?? "";
                        },
                    },
                },
                formatter: (params) => {
                    const safeParams = Array.isArray(params)
                        ? params
                        : [params];
                    const index = safeParams[0]?.dataIndex ?? 0;
                    const axisDate = chartSeries.axisDates[index];
                    const axisKey = chartSeries.axisKeys[index];
                    const activePeriod = chartSeries.activePeriods[index];
                    const exactManualPoint =
                        chartSeries.exactManualPointMap.get(axisKey);

                    const rows = [
                        `<strong>${formatDisplayDate(axisDate, dateGranularity)}</strong>`,
                    ];

                    if (activePeriod) {
                        rows.push(
                            `Mode: ${getModeLabel(activePeriod.TargetMode)}`,
                        );
                        rows.push(
                            `Percentage: ${Number(activePeriod.PercentageN ?? 0)}%`,
                        );
                    } else {
                        rows.push("Mode: —");
                        rows.push("Percentage: —");
                    }

                    rows.push(
                        `Percentage preview: ${formatTooltipNumber(
                            chartSeries.percentagePreviewData[index],
                        )}`,
                    );
                    rows.push(
                        `Target: ${formatTooltipNumber(
                            chartSeries.calculatedTargetData[index],
                        )}`,
                    );
                    rows.push(
                        `Manual point: ${
                            exactManualPoint
                                ? formatTooltipNumber(
                                      exactManualPoint.TargetValue,
                                  )
                                : "—"
                        }`,
                    );

                    return rows.join("<br/>");
                },
            },
            grid: {
                left: 50,
                right: 40,
                top: 60,
                bottom: 50,
                containLabel: true,
            },
            xAxis: {
                type: "category",
                data: chartSeries.axisKeys,
                boundaryGap: false,
                axisLabel: {
                    hideOverlap: true,
                    formatter: (value) =>
                        formatDisplayDate(value, dateGranularity),
                },
            },
            yAxis: {
                type: "value",
                scale: true,
            },
            series: [
                {
                    name: "Target",
                    type: "line",
                    smooth: dateGranularity === TARGET_GRANULARITY_DAY,
                    connectNulls: false,
                    showSymbol: false,
                    data: chartSeries.calculatedTargetData,
                    markArea: markAreaData.length
                        ? {
                              silent: true,
                              itemStyle: {
                                  opacity: 1,
                              },
                              data: markAreaData,
                          }
                        : undefined,
                    z: 3,
                },
                {
                    name: "Percentage preview",
                    type: "line",
                    smooth: dateGranularity === TARGET_GRANULARITY_DAY,
                    connectNulls: false,
                    showSymbol: false,
                    data: chartSeries.percentagePreviewData,
                    z: 2,
                },
                {
                    name: "Manual points",
                    type: "scatter",
                    symbolSize: 10,
                    data: chartSeries.manualPointsScatterData,
                    z: 4,
                },
                {
                    name: "Draft point",
                    type: "scatter",
                    symbolSize: 12,
                    data: hoveredPoint
                        ? [
                              {
                                  value: [
                                      hoveredPoint.targetDate,
                                      hoveredPoint.targetValue,
                                  ],
                              },
                          ]
                        : [],
                    tooltip: { show: false },
                    z: 5,
                },
            ],
        };
    }, [chartSeries, dateGranularity, hoveredPoint]);

    const buildDraftPointFromEvent = useCallback(
        (zrEvent, chartInstance) => {
            if (!chartInstance || !chartSeries.axisDates.length) return null;

            const pointInPixel = [zrEvent.offsetX, zrEvent.offsetY];

            if (!chartInstance.containPixel({ gridIndex: 0 }, pointInPixel)) {
                return null;
            }

            const converted = chartInstance.convertFromPixel(
                { gridIndex: 0 },
                pointInPixel,
            );

            if (!Array.isArray(converted) || converted.length < 2) return null;

            const rawX = converted[0];
            const rawY = Number(converted[1]);

            if (Number.isNaN(rawY)) return null;

            let nearestIndex =
                typeof rawX === "string"
                    ? chartSeries.axisKeys.indexOf(rawX)
                    : Math.round(Number(rawX));

            if (nearestIndex < 0) return null;

            nearestIndex = clamp(
                nearestIndex,
                0,
                chartSeries.axisDates.length - 1,
            );

            const axisDate = chartSeries.axisDates[nearestIndex];
            if (!axisDate) return null;

            return {
                targetDate: axisDate.format("YYYY-MM-DD"),
                targetValue: Math.max(0, Math.round(rawY)),
                targetType,
                targetSubtype,
                label: formatDisplayDate(axisDate, dateGranularity),
                key: chartSeries.axisKeys[nearestIndex],
            };
        },
        [
            chartSeries.axisDates,
            chartSeries.axisKeys,
            dateGranularity,
            targetSubtype,
        ],
    );

    useEffect(() => {
        if (viewMode !== "chart") return undefined;

        const chartInstance = chartRef.current?.getEchartsInstance();
        if (!chartInstance) return undefined;

        const zr = chartInstance.getZr();
        const dom = chartInstance.getDom();

        const handleMouseMove = (event) => {
            const nextDraftPoint = buildDraftPointFromEvent(
                event,
                chartInstance,
            );

            setHoveredPoint((previous) => {
                if (
                    previous?.key === nextDraftPoint?.key &&
                    previous?.targetValue === nextDraftPoint?.targetValue
                ) {
                    return previous;
                }

                if (!previous && !nextDraftPoint) return previous;

                return nextDraftPoint;
            });
        };

        const handleGlobalOut = () => {
            setHoveredPoint(null);
        };

        const handleClick = (event) => {
            const nextDraftPoint = buildDraftPointFromEvent(
                event,
                chartInstance,
            );
            if (!nextDraftPoint) return;

            onCreatePoint(nextDraftPoint);
        };

        const handleContextMenu = (event) => {
            const nextDraftPoint = buildDraftPointFromEvent(
                event,
                chartInstance,
            );
            if (!nextDraftPoint) return;

            const existingPoint = chartSeries.exactManualPointMap.get(
                nextDraftPoint.key,
            );

            if (existingPoint) onDeletePoint(existingPoint);
        };

        const preventBrowserContextMenu = (nativeEvent) => {
            nativeEvent.preventDefault();
        };

        zr.on("mousemove", handleMouseMove);
        zr.on("globalout", handleGlobalOut);
        zr.on("click", handleClick);
        zr.on("contextmenu", handleContextMenu);
        dom.addEventListener("contextmenu", preventBrowserContextMenu);

        return () => {
            try {
                zr.off("mousemove", handleMouseMove);
                zr.off("globalout", handleGlobalOut);
                zr.off("click", handleClick);
                zr.off("contextmenu", handleContextMenu);
                dom.removeEventListener(
                    "contextmenu",
                    preventBrowserContextMenu,
                );
            } catch (error) {
                // Ignore cleanup errors from disposed zrender instance.
            }
        };
    }, [
        buildDraftPointFromEvent,
        chartSeries.exactManualPointMap,
        onCreatePoint,
        onDeletePoint,
        viewMode,
    ]);

    const visibleTableIndexes = useMemo(() => {
        return chartSeries.axisDates
            .map((item, index) => index)
            .filter((index) => {
                if (tableDensity !== "compact") return true;

                return tableRows.some((row) =>
                    row.series.exactManualPointMap.has(
                        row.series.axisKeys[index],
                    ),
                );
            });
    }, [chartSeries.axisDates, tableDensity, tableRows]);

    const monthGroups = useMemo(() => {
        const groups = [];

        visibleTableIndexes.forEach((index) => {
            const label = chartSeries.axisDates[index].format("MMMM YYYY");
            const lastGroup = groups[groups.length - 1];

            if (lastGroup?.label === label) {
                lastGroup.colSpan += 1;
            } else {
                groups.push({
                    label,
                    colSpan: 1,
                    startsNewMonth: groups.length > 0,
                });
            }
        });

        return groups;
    }, [chartSeries.axisDates, visibleTableIndexes]);

    const monthStartIndexes = useMemo(() => {
        return new Set(
            visibleTableIndexes.filter((index, visibleIndex) => {
                if (
                    dateGranularity !== TARGET_GRANULARITY_DAY ||
                    !visibleIndex
                ) {
                    return false;
                }

                return (
                    chartSeries.axisDates[index].format("YYYY-MM") !==
                    chartSeries.axisDates[
                        visibleTableIndexes[visibleIndex - 1]
                    ].format("YYYY-MM")
                );
            }),
        );
    }, [chartSeries.axisDates, dateGranularity, visibleTableIndexes]);

    const handleAddPoint = () => {
        const axisDate = chartSeries.axisDates[0] ?? dayjs.utc().startOf("day");

        onCreatePoint({
            targetDate: axisDate.format("YYYY-MM-DD"),
            targetValue: 1,
            targetType,
            targetSubtype,
            label: formatDisplayDate(axisDate, dateGranularity),
            key: chartSeries.axisKeys[0],
        });
    };

    const renderTableCell = (metric, index) => {
        const axisDate = chartSeries.axisDates[index];
        const axisKey = chartSeries.axisKeys[index];
        const activePeriod = metric.series.activePeriods[index];
        const exactManualPoint = metric.series.exactManualPointMap.get(axisKey);
        const value = exactManualPoint
            ? round2(exactManualPoint.TargetValue)
            : metric.series.calculatedTargetData[index];

        const mode = String(
            activePeriod?.TargetMode || (exactManualPoint ? "MANUAL" : ""),
        ).toUpperCase();

        const percentage = activePeriod?.PercentageN;
        const hasValue =
            value !== null && value !== undefined && !Number.isNaN(value);

        const tooltipTitle = (
            <Box>
                <Typography
                    variant="caption"
                    sx={{ fontWeight: metric.selected ? 700 : 400 }}
                >
                    {metric.label} ·{" "}
                    {formatDisplayDate(axisDate, dateGranularity)}
                </Typography>
                <Typography variant="caption" display="block">
                    Mode: {mode ? getModeLabel(mode) : "—"}
                </Typography>
                <Typography variant="caption" display="block">
                    Percentage:{" "}
                    {percentage !== null && percentage !== undefined
                        ? `${Number(percentage)}%`
                        : "—"}
                </Typography>
                <Typography variant="caption" display="block">
                    Target: {formatTooltipNumber(value)}
                </Typography>
                <Typography variant="caption" display="block">
                    Manual point:{" "}
                    {exactManualPoint
                        ? formatTooltipNumber(exactManualPoint.TargetValue)
                        : "—"}
                </Typography>
                <Typography variant="caption" display="block">
                    Value type: {exactManualPoint ? "Manual" : "Calculated"}
                </Typography>
            </Box>
        );

        return (
            <Box
                key={`${metric.value}-${axisKey}`}
                component="td"
                sx={{
                    borderLeft: monthStartIndexes.has(index)
                        ? "2px solid rgba(255,255,255,0.35)"
                        : undefined,
                    borderRight: "1px solid rgba(255,255,255,0.08)",
                    borderBottom: "1px solid rgba(255,255,255,0.08)",
                    minWidth:
                        dateGranularity === TARGET_GRANULARITY_DAY
                            ? tableDayColumnWidth
                            : 120,
                    p: 0,
                }}
            >
                <Tooltip arrow title={tooltipTitle}>
                    <Box
                        onClick={() => {
                            if (exactManualPoint) {
                                onEditPoint(exactManualPoint);
                                return;
                            }

                            onCreatePoint({
                                targetDate: axisDate.format("YYYY-MM-DD"),
                                targetValue: Math.max(
                                    1,
                                    Math.round(Number(value ?? 1)),
                                ),
                                targetType,
                                targetSubtype: metric.value,
                                label: formatDisplayDate(
                                    axisDate,
                                    dateGranularity,
                                ),
                                key: axisKey,
                            });
                        }}
                        sx={{
                            position: "relative",
                            minHeight: 56,
                            px: 1,
                            py: 0.75,
                            cursor: "pointer",
                            textAlign: "center",
                            backgroundColor: getModeColor(mode),
                            "&:hover": {
                                outline: "1px solid rgba(255,255,255,0.35)",
                            },
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: exactManualPoint ? 800 : 400,
                                opacity: hasValue ? 1 : 0.55,
                            }}
                        >
                            {hasValue ? formatTooltipNumber(value) : "—"}
                        </Typography>

                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{
                                fontWeight: exactManualPoint ? 800 : 400,
                                opacity: hasValue ? 1 : 0.55,
                            }}
                        >
                            {getModeShortLabel(mode, percentage)}
                        </Typography>
                    </Box>
                </Tooltip>
            </Box>
        );
    };

    return (
        <Stack spacing={2}>
            <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1}
                alignItems={{ xs: "stretch", md: "center" }}
                justifyContent="space-between"
            >
                <Typography variant="body2" sx={{ fontSize: "0.8em" }}>
                    {viewMode === "chart"
                        ? "Left click creates or updates a manual point using the clicked Y value. Right click deletes the manual point for that date if it exists."
                        : "Click a cell to create or edit a manual point. Manual values are bold; calculated values are regular."}
                </Typography>

                {viewMode === "table" ? (
                    <Stack direction="row" spacing={1}>
                        <Button
                            size="small"
                            variant="contained"
                            onClick={handleAddPoint}
                        >
                            {/* Manual point */}
                            <AddIcon />
                        </Button>

                        <ToggleButtonGroup
                            exclusive
                            size="small"
                            value={tableDensity}
                            onChange={(event, nextValue) => {
                                if (nextValue) setTableDensity(nextValue);
                            }}
                        >
                            <ToggleButton
                                value="compact"
                                aria-label="Compact table"
                            >
                                <Tooltip
                                    arrow
                                    title="Compact: show only dates with manual points"
                                >
                                    <Box
                                        component="span"
                                        sx={{ display: "inline-flex" }}
                                    >
                                        <ViewCompactIcon fontSize="small" />
                                    </Box>
                                </Tooltip>
                            </ToggleButton>

                            <ToggleButton value="full" aria-label="Full table">
                                <Tooltip
                                    arrow
                                    title="Full: show every date in the range"
                                >
                                    <Box
                                        component="span"
                                        sx={{ display: "inline-flex" }}
                                    >
                                        <ViewComfyIcon fontSize="small" />
                                    </Box>
                                </Tooltip>
                            </ToggleButton>
                        </ToggleButtonGroup>
                    </Stack>
                ) : null}
            </Stack>

            {viewMode === "chart" ? (
                loading ? (
                    <Skeleton
                        variant="rounded"
                        animation="wave"
                        height={420}
                        sx={{ transform: "none" }}
                    />
                ) : (
                    <EChartsReact
                        ref={chartRef}
                        option={chartOption}
                        style={{ height: 420, width: "100%" }}
                    />
                )
            ) : (
                <Box
                    sx={{
                        border: "1px solid rgba(255,255,255,0.12)",
                        borderRadius: 1,
                        maxHeight: 540,
                        overflow: "auto",
                        scrollbarColor: `rgba(70, 70, 70, 0.35) ${Colours.notificationCard}`,
                        "&::-webkit-scrollbar, &::-webkit-scrollbar-corner": {
                            backgroundColor: Colours.notificationCard,
                        },
                        "&::-webkit-scrollbar-track": {
                            backgroundColor: Colours.notificationCard,
                        },
                    }}
                >
                    <Box
                        component="table"
                        sx={{
                            borderCollapse: "separate",
                            borderSpacing: 0,
                            width: "max-content",
                            minWidth: "100%",
                        }}
                    >
                        <Box component="thead">
                            <Box component="tr">
                                <Box
                                    component="th"
                                    rowSpan={
                                        dateGranularity ===
                                        TARGET_GRANULARITY_DAY
                                            ? 2
                                            : 1
                                    }
                                    sx={{
                                        position: "sticky",
                                        top: 0,
                                        left: 0,
                                        zIndex: 5,
                                        width: tableFirstColumnWidth,
                                        minWidth: tableFirstColumnWidth,
                                        maxWidth: tableFirstColumnWidth,
                                        boxSizing: "border-box",
                                        p: 1,
                                        textAlign: "left",
                                        backgroundColor:
                                            Colours.notificationCard,
                                        borderRight:
                                            "1px solid rgba(255,255,255,0.16)",
                                        borderBottom:
                                            "1px solid rgba(255,255,255,0.12)",
                                    }}
                                >
                                    <Typography variant="body2">
                                        Target{" "}
                                        {getDateGranularityLabel(
                                            dateGranularity,
                                        )}
                                    </Typography>

                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                    >
                                        {getTargetTypeLabel(targetType)}
                                    </Typography>
                                </Box>

                                {dateGranularity === TARGET_GRANULARITY_DAY
                                    ? monthGroups.map((group) => (
                                          <Box
                                              key={group.label}
                                              component="th"
                                              colSpan={group.colSpan}
                                              sx={{
                                                  position: "sticky",
                                                  top: 0,
                                                  left: tableFirstColumnWidth,
                                                  zIndex: 4,
                                                  height: tableHeaderRowHeight,
                                                  p: 1,
                                                  minWidth: `${group.colSpan * tableDayColumnWidth}px`,
                                                  textAlign: "left",
                                                  backgroundColor:
                                                      Colours.notificationCard,
                                                  backdropFilter: "blur(6px)",
                                                  borderLeft:
                                                      group.startsNewMonth
                                                          ? "2px solid rgba(255,255,255,0.35)"
                                                          : undefined,
                                                  borderRight:
                                                      "1px solid rgba(255,255,255,0.12)",
                                                  borderBottom:
                                                      "1px solid rgba(255,255,255,0.12)",
                                              }}
                                          >
                                              {group.label}
                                          </Box>
                                      ))
                                    : visibleTableIndexes.map((index) => (
                                          <Box
                                              key={chartSeries.axisKeys[index]}
                                              component="th"
                                              sx={{
                                                  minWidth: 120,
                                                  p: 1,
                                                  textAlign: "center",
                                                  backgroundColor:
                                                      Colours.notificationCard,
                                                  borderRight:
                                                      "1px solid rgba(255,255,255,0.12)",
                                                  borderBottom:
                                                      "1px solid rgba(255,255,255,0.12)",
                                              }}
                                          >
                                              {formatDisplayDate(
                                                  chartSeries.axisDates[index],
                                                  dateGranularity,
                                              )}
                                          </Box>
                                      ))}
                            </Box>

                            {dateGranularity === TARGET_GRANULARITY_DAY ? (
                                <Box component="tr">
                                    {visibleTableIndexes.map((index) => (
                                        <Box
                                            key={chartSeries.axisKeys[index]}
                                            component="th"
                                            sx={{
                                                position: "sticky",
                                                top: tableHeaderRowHeight,
                                                zIndex: 3,
                                                borderLeft:
                                                    monthStartIndexes.has(index)
                                                        ? "2px solid rgba(255,255,255,0.35)"
                                                        : undefined,
                                                minWidth: tableDayColumnWidth,
                                                p: 0.75,
                                                textAlign: "center",
                                                backgroundColor:
                                                    Colours.notificationCard,
                                                borderRight:
                                                    "1px solid rgba(255,255,255,0.12)",
                                                borderBottom:
                                                    "1px solid rgba(255,255,255,0.12)",
                                            }}
                                        >
                                            {chartSeries.axisDates[
                                                index
                                            ].format("D")}
                                        </Box>
                                    ))}
                                </Box>
                            ) : null}
                        </Box>

                        <Box component="tbody">
                            {tableRows.map((metric) => (
                                <Box component="tr" key={metric.value}>
                                    <Box
                                        component="th"
                                        sx={{
                                            position: "sticky",
                                            left: 0,
                                            zIndex: 4,
                                            width: tableFirstColumnWidth,
                                            minWidth: tableFirstColumnWidth,
                                            maxWidth: tableFirstColumnWidth,
                                            boxSizing: "border-box",
                                            p: 1,
                                            textAlign: "left",
                                            backgroundColor:
                                                Colours.notificationCard,
                                            borderRight:
                                                "1px solid rgba(255,255,255,0.16)",
                                            borderBottom:
                                                "1px solid rgba(255,255,255,0.08)",
                                        }}
                                    >
                                        {metric.label}
                                    </Box>

                                    {visibleTableIndexes.map((index) =>
                                        renderTableCell(metric, index),
                                    )}
                                </Box>
                            ))}
                        </Box>
                    </Box>

                    {!visibleTableIndexes.length ? (
                        <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{ p: 2 }}
                        >
                            No manual points in compact mode.
                        </Typography>
                    ) : null}
                </Box>
            )}
        </Stack>
    );
};

export default ManualTargetsVisualizer;
